import { message } from 'antd';
import { REFRIGERANT_EVENTS_CRUD_ACTION_CREATORS } from '../actions/refrigerant_events_actions';
import { ASSETS_CRUD_ACTION_CREATORS } from '../actions/assets_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { logoutSuccess } from '../actions/session_actions';
import { ROLE_TYPES } from '../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import {
	REFRIGERANT_BUCKETS,
	REFRIGERANT_EVENT_TYPE_ORDER,
	REFRIGERANT_SERVER_BUCKET_NAMES,
	withoutRefrigerantBucketSelection,
} from '../utils/RefrigerantUtils';

const fetchUtils = new FetchUtils();

/**
 * The EPA Section 608 servicing ledger.
 *
 * APPEND ONLY — there is deliberately no `update` or `delete` route on either persona, and a
 * correction is a new event rather than an edit, so the servicing record an auditor reads keeps the
 * full history including the disagreement. Do not add `updateEvent`/`deleteEvent` here: the server
 * defines those handlers unconditionally and only the missing routes stop them working.
 *
 * Writes are buyer + internal-tech only. A third-party supplier's POST is refused by the server
 * (`SupplierRefrigerantEventController` requires a `privateBuyerCompanyId`), and its reads fail
 * closed onto an empty list, so a third party sees an empty ledger rather than an error.
 */
export const refrigerantEventsRestCrudThunksForBuyer = new RestCrudThunks(
	'refrigerant_events',
	'refrigerant_event',
	'refrigerantEvent',
	'refrigerantEvents',
	REFRIGERANT_EVENTS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER,
	false,
	'asset/'
);

export const refrigerantEventsRestCrudThunksForSupplier = new RestCrudThunks(
	'refrigerant_events',
	'refrigerant_event',
	'refrigerantEvent',
	'refrigerantEvents',
	REFRIGERANT_EVENTS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER,
	false,
	'asset/'
);

export const refrigerantEventsRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? refrigerantEventsRestCrudThunksForSupplier
		: refrigerantEventsRestCrudThunksForBuyer;

/**
 * One appliance's derived compliance numbers — leak rate, band, CO2e, next inspection, verification
 * due.
 *
 * Computed from MySQL rather than read from the OpenSearch projection, so it is exact and always
 * fresh; when the index screen and this disagree, this is the authoritative one. Resolves `null`
 * rather than rejecting when the appliance has no summary (not tracked, or not visible to the
 * caller) so a detail tab can render an empty state instead of an error toast.
 */
export const retrieveRefrigerantAssetSummary = (userType) => (assetId) => {
	const apiUrl = `${getBackendUri()}/api/v1/${userType}/asset/refrigerant_events/asset_summary/${assetId}`;
	return (dispatch) =>
		new Promise((resolve, reject) => {
			fetchUtils
				.get(apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
						reject('unauthorized');
						return;
					}
					if (status === 404 || nullSafeGet('status', json) === 'error') {
						resolve(null);
						return;
					}
					resolve(nullSafeGet('data', json));
				})
				.catch((error) => reject(nullSafeGet('message', error)));
		});
};

/**
 * The bucket counts live on the ASSETS slice, not the events slice: `PipelineFilter` is handed
 * `stateSlice.counts`, and the state slice behind the Assets sub-tab's table is `state.assets`.
 */
const bucketCountActionCreators = ASSETS_CRUD_ACTION_CREATORS;

/**
 * The Assets sub-tab's bucket counts.
 *
 * Two requests, not one: the server aggregates the five overlapping compliance buckets in a single
 * `.size(0)` call, and `inactive` has no bucket there — the aggregation runs over the active set —
 * so its count comes from a second search whose only job is the total.
 *
 * `filters` must already be free of the bucket selection (the counts describe the whole tracked set,
 * not the bucket the user is standing on) — `withoutRefrigerantBucketSelection` is applied here so a
 * caller cannot forget it and silently drive the other five counts to zero.
 *
 * Every bucket is dispatched, including the ones the response omits: the aggregation returns only
 * the buckets it has documents for, and a chip whose count is `undefined` renders blank rather than
 * `0`.
 */
export const refreshRefrigerantBandCounts = (userType) => (filters) => (dispatch) => {
	const baseUrl = getBackendUri();
	const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
	const scopedFilters = {
		...withoutRefrigerantBucketSelection(filters),
		isRefrigerantTracked: true,
		// The bands partition the ACTIVE tracked set, matching the list, and `updateFilters` on the
		// server would otherwise inject isActive=true anyway.
		noActiveFilter: true,
		isActive: 'true',
	};

	const dispatchCount = (bucketName, count) =>
		dispatch(bucketCountActionCreators.countBySuccess(count, bucketName));

	const bandCounts = fetchUtils
		.post(`${baseUrl}/api/v1/${userType}/asset/assets/refrigerant/band_counts`, {
			headers,
			data: scopedFilters,
		})
		.then(parseJSON)
		.then(({ json, status }) => {
			if (status === 401) {
				dispatch(logoutSuccess());
				return;
			}
			if (nullSafeGet('status', json) === 'error') {
				message.error(nullSafeGetOrElse('message', json, 'Could not load refrigerant counts.'));
				return;
			}
			const countByBucket = nullSafeGetOrElse('data', json, []).reduce(
				(accumulator, category) => ({
					...accumulator,
					[category.categoryName]: category.count,
				}),
				{}
			);
			REFRIGERANT_SERVER_BUCKET_NAMES.forEach((bucketName) =>
				dispatchCount(bucketName, nullSafeGetOrElse(bucketName, countByBucket, 0))
			);
		});

	// The inactive bucket is a separate partition from the bands, so it needs its own scope —
	// `noActiveFilter` to stop the forced isActive=true, then isActive=false to select it. Only the
	// envelope's `count` is read; `limit=1` rather than 0 because a zero page size is not a shape the
	// pagination layer is exercised with anywhere else.
	const inactiveCount = fetchUtils
		.post(`${baseUrl}/api/v1/${userType}/asset/assets/search?offset=0&limit=1`, {
			headers,
			data: {
				...withoutRefrigerantBucketSelection(filters),
				isRefrigerantTracked: true,
				noActiveFilter: true,
				isActive: 'false',
			},
		})
		.then(parseJSON)
		.then(({ json, status }) => {
			if (status === 401 || nullSafeGet('status', json) === 'error') return;
			dispatchCount(REFRIGERANT_BUCKETS.INACTIVE, nullSafeGetOrElse('count', json, 0));
		});

	return Promise.all([bandCounts, inactiveCount]).catch(() => undefined);
};

/**
 * The Event Log's per-type counts.
 *
 * There is no `count_by_event_type` aggregation on the ledger — only the generic `count_by`, which
 * returns a scalar for whatever filters it is given — so this is five parallel counts, one per type.
 * Cheap: the table is indexed on `(buyer_company_id, occurred_at)` and the query is a COUNT.
 *
 * Both `eventType` and `eventTypes` are stripped from the incoming filters, for the same reason the
 * band counts strip the bucket: the counts describe the whole ledger, so the chip the user is
 * standing on must not narrow the other four.
 */
export const refreshRefrigerantEventTypeCounts = (userType) => (filters) => (dispatch) => {
	const thunks = refrigerantEventsRestCrudThunks(userType);
	const { eventType, eventTypes, ...scopedFilters } = filters || ({} as any);

	return Promise.all(
		REFRIGERANT_EVENT_TYPE_ORDER.map((type) =>
			dispatch(thunks.countBy({ ...scopedFilters, eventType: type }, type))
		)
	).catch(() => undefined);
};

/**
 * The company-wide leak rate shown in the Assets sub-tab header — total pounds added in the trailing
 * 12 months over the total full charge of the tracked estate, as one ratio rather than a mean of
 * per-asset rates.
 *
 * POST for the same reason `band_counts` is: it takes the same filter bag, which can carry long id
 * lists. The server strips the bucket selection and scopes to tracked + active itself, so the
 * headline cannot follow the tab the user is standing on.
 *
 * Resolves `null` rather than rejecting on any failure, and stays silent — no toast. The tile is
 * supplementary to the list it sits above, so a failed aggregation should cost the figure, not
 * interrupt the screen. It must never fall back to a client-side estimate: a compliance number
 * derived from one page of results would be wrong in a way nobody could see.
 *
 * Response: `{ "type": "RefrigerantCorporateLeakRate", "data": { "addedLbTrailing12Mo": 1234.5,
 * "fullChargeLb": 6270, "leakRatePercent": 19.7, "trackedAssetCount": 42 }, "status": "ok" }`.
 * `leakRatePercent` is absent when nothing in scope carries a full charge — undefined, not zero.
 */
export const retrieveCorporateRefrigerantLeakRate = (userType) => (filters) => {
	const apiUrl = `${getBackendUri()}/api/v1/${userType}/asset/assets/refrigerant/corporate_leak_rate`;
	return () =>
		new Promise((resolve) => {
			fetchUtils
				.post(apiUrl, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					data: withoutRefrigerantBucketSelection(filters),
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					if (status !== 200 || nullSafeGet('status', json) === 'error') {
						resolve(null);
						return;
					}
					resolve(nullSafeGet('data', json));
				})
				.catch(() => resolve(null));
		});
};
